/* -------------------------------------------------------------------------- */
/*          Componente SlidingPaneUnitHistoricalPositionsReport               */
/* -------------------------------------------------------------------------- */
// Este componente contiene un Sidebar para subir una imagen.

import * as authSelectors from '@redux/reducers/auth';
import { divIcon, latLng, latLngBounds } from 'leaflet';
import isEmpty from 'lodash/isEmpty';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { MapPin } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Marker, Polyline, Popup, Tooltip, useMap } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import SlidingPane from 'react-sliding-pane';
import { Button, Col, Label, Media, Row, Spinner } from 'reactstrap';
import Icon from 'utility/icomoon';
import { TableColumn } from 'utility/types';
import { formatColumnsForDataSource, numberWithCommas } from 'utility/Utils';
import RotatedMarker from 'views/components/Map/components/RotatedMarker';
import ReportViewerButton from 'views/components/ReportViewerButton';
import Table from 'views/components/Table';
import Toolbar from 'views/components/Toolbar';
import DateInput from 'views/formComponents/DateInput';
import SwitchInput from 'views/formComponents/SwitchInput';
import UnitsDropdown from 'views/formComponents/UnitsDropdown';
import DefaultButton from '../../../../components/DefaultButton';
import * as unitActions from '../../actions';
import * as unitSelectors from '../../reducers';
import { UNIT_IDLE_SPEED } from '../../types';
import { getDatesDifferenceString, getIconByDatesDifference } from '../../utils';
import { env } from '@src/env';
import { generateAndDownloadKML } from '@src/utility/exportKML';
import SlidingPaneForm from 'views/formComponents/SlidingPaneForm';
import * as geofenceActions from 'views/screens/Geofences/actions';
import * as geofenceSelectors from 'views/screens/Geofences/reducers';
import geofencePermissions from 'views/screens/Geofences/permissions';
import { getExtraGeofenceSaveValues } from 'views/screens/Geofences/utils';
import { v4 as uuidv4 } from 'uuid';
import * as alerts from '@redux/utils/alerts';

/* -------------------------------------------------------------------------- */
/*                 SlidingPaneUnitHistoricalPositionsReport                   */
/* -------------------------------------------------------------------------- */

export interface ShowHistoricalPositions {
  show: boolean;
  showUnitHistoryMarkers?: boolean;
  showUnitHistoryParkedMarkers?: boolean;
  showUnitHistoryConsoleEventMarkers?: boolean;
  showUnitHistoryOnlyNormalPositionsMarkers?: boolean;
  useInterval?: number;
  reportType?: number;
  startDate?: string;
  endDate?: string;
  idleSpeed?: number;
  selectedPositionId?: any;
  lineWidth?: any;
}

interface Props {
  closeSlidingPane: Function;
  isSlidingPaneOpen: boolean;
  showHistoricalPositions?: ShowHistoricalPositions;
  title: string;
  subtitle: string;
  unit: any;
  setMapPositionToPositionsBounds: boolean;
  changeSetMapPositionToUnitsBounds: Function;
  selectedUnit?: any;
  setSelectedUnit?: Function;
  selectedUnitRef?: any;
  width?: string;
}
const SlidingPaneUnitHistoricalPositionsReport = (props: Props) => {
  const intl = useIntl();
  const columnInfo: TableColumn[] = [
    {
      fieldName: 'eventName',
      template: (position) =>
        position.eventId != null ? (
          <Media key={position.id}>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                background: 'white',
                width: 20,
                height: 20,
                padding: 3,
                borderRadius: '100%',
                marginRight: '0.2rem',
                borderStyle: 'solid',
                borderColor: `#${position.eventColor}`
              }}
            >
              <Icon
                icon={position.eventId !== 0 ? `Alert-${position.eventId}` : 'MapPin'}
                color={`#${position.eventColor}`}
                size={20}
              />
            </div>
            <FormattedMessage id={`organizations.event${position.eventId}`} />
          </Media>
        ) : (
          <Media key={position.id} className="d-flex align-items-center justify-content-start">
            <Icon
              icon={getIconByDatesDifference(position.endDate, position.gpsDate)}
              size={32}
              style={{
                margin: '-15px -3px -15px -7px'
              }}
            />
            {position.eventId ? (
              <FormattedMessage id={`organizations.event${position.eventId}`} />
            ) : (
              position.eventName
            )}
          </Media>
        ),
      maxWidth: '150',
      headerText: 'units.event'
    },
    {
      fieldName: 'locationName',
      template: (position) => (
        <Media key={position.id} className="d-flex align-items-center justify-content-start">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              marginRight: '0.2rem'
            }}
          >
            <Icon icon={getArrowImage(position.heading)} size={17} />
          </div>
          <span>{position.locationName}</span>
        </Media>
      ),
      maxWidth: '150',
      headerText: 'units.locationName'
    },
    {
      fieldName: 'gpsDate',
      maxWidth: '150',
      headerText: 'units.date',
      type: 'dateTime',
      formatColumn: 'yyyy-MM-dd HH:mm'
    }
  ];
  function formatIgnitionStatus(ignitionValue) {
    if (ignitionValue === 1) {
      return intl.formatMessage({ id: 'units.on' });
    } else if (ignitionValue === 0) {
      return intl.formatMessage({ id: 'units.off' });
    }
    return '--';
  }

  const {
    closeSlidingPane,
    isSlidingPaneOpen,
    showHistoricalPositions,
    title,
    subtitle,
    setMapPositionToPositionsBounds,
    unit,
    changeSetMapPositionToUnitsBounds,
    selectedUnit,
    setSelectedUnit,
    selectedUnitRef
  } = props;

  const extraColumns: TableColumn[] = [
    {
      fieldName: 'gpsDate',
      headerText: 'units.date',
      type: 'dateTime',
      formatColumn: 'yyyy-MM-dd HH:mm'
    },
    {
      fieldName: 'systemDate',
      headerText: 'units.receptionDate',
      type: 'dateTime',
      formatColumn: 'yyyy-MM-dd HH:mm'
    },
    {
      fieldName: 'eventName',
      template: (position) => {
        return position.eventId != null ? (
          <Media key={position.id}>
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                background: 'white',
                width: 20,
                height: 20,
                padding: 3,
                borderRadius: '100%',
                marginRight: '0.2rem',
                borderStyle: 'solid',
                borderColor: `#${position.eventColor}`
              }}
            >
              <Icon
                icon={position.eventId !== null ? `Alert-${position.eventId}` : 'MapPin'}
                color={`#${position.eventColor}`}
                size={20}
              />
            </div>
            <FormattedMessage id={`organizations.event${position.eventId}`} />
          </Media>
        ) : (
          <Media key={position.id} className="d-flex align-items-center justify-content-start">
            <Icon
              icon={getIconByDatesDifference(position.endDate, position.gpsDate)}
              size={32}
              style={{
                margin: '-15px -3px -15px -7px'
              }}
            />
            {position.eventName}
          </Media>
        );
      },
      headerText: 'units.event'
    },
    {
      fieldName: 'locationName',
      template: (position) => (
        <Media key={position.id} className="d-flex align-items-center justify-content-start">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              marginRight: '0.2rem'
            }}
          >
            <Icon icon={getArrowImage(position.heading)} size={17} />
          </div>
          <span>{position.locationName}</span>
        </Media>
      ),
      maxWidth: 150,
      headerText: 'units.locationName'
    },
    {
      fieldName: 'speed',
      headerText: 'units.speed',
      formatColumn: '0,0',
      type: 'number'
    },
    {
      fieldName: 'metric',
      headerText: unit.defaultMetricName + '(' + unit.defaultMetricUnit + ')',
      formatColumn: '0.00',
      type: 'number'
    },
    {
      fieldName: 'isIgnitionOn',
      headerText: 'sensors.ignition',
      format: formatIgnitionStatus,
      visible: unit.hasIgnitionInput,
      type: 'string'
    },
    {
      fieldName: 'odometer',
      headerText: 'units.odometer',
      format: (value: any) => numberWithCommas(value),
      type: 'number'
    }
  ];

  const { control, watch, reset, handleSubmit } = useForm();
  const formValues = watch();

  const dispatch = useDispatch();

  const style = getComputedStyle(document.body);
  const primary = env.VITE_PRIMARY_COLOR;
  const [selected, changeSelected] = useState<any[any]>([]);
  const fetchInterval = useRef<any>();
  const [size, changeSize] = useState<string>(props.width || '45rem');
  const [viewTable, changeViewTable] = useState<boolean>(true);
  const [search, setSearch] = useState<Date>(new Date());
  const [rerender, changeRerender] = useState<Date>(new Date());
  const [historicalPositionsOnMapBounds, setHistoricalPositionsOnMapBounds] = useState<any>(null);
  const [isCreateGeofenceSlideOpen, setIsCreateGeofenceSlideOpen] = useState(false);
  const [editingGeofence, setEditingGeofence] = useState<any>(null);
  const editingGeofenceRef = useRef<any>(null);

  const {
    isFetching,
    user,
    dropDownUnit,
    parsers,
    userDistanceFormat,
    userSpeedFormat,
    isAddingGeofence
  } = useSelector((state) => ({
    isFetching: unitSelectors.isFetchingUnitPositions(state),
    dropDownUnit: formValues?.selectedUnit
      ? unitSelectors.getUnit(state, formValues?.selectedUnit)
      : undefined,
    user: authSelectors.getUser(state),
    parsers: authSelectors.getParsers(state),
    userDistanceFormat: authSelectors.getAuthSystems(state).distance,
    userSpeedFormat: authSelectors.getAuthSystems(state).speed,
    isAddingGeofence: geofenceSelectors.isAddingGeofence(state)
  }));

  // Update ref when editing geofence changes
  useEffect(() => {
    editingGeofenceRef.current = editingGeofence;
  }, [editingGeofence]);

  const wasAddingRef = useRef(false);

  // Handle geofence creation completion
  useEffect(() => {
    if (isAddingGeofence) {
      wasAddingRef.current = true;
    } else if (wasAddingRef.current && !isAddingGeofence && isCreateGeofenceSlideOpen) {
      // Geofence creation completed successfully
      wasAddingRef.current = false;
      setIsCreateGeofenceSlideOpen(false);
      setEditingGeofence(null);

      alerts.showSuccessMessage({
        success: intl.formatMessage({ id: 'geofences.createdSuccessfully' })
      });
    }
  }, [isAddingGeofence, isCreateGeofenceSlideOpen, intl]);

  useEffect(() => {
    if (
      formValues?.selectedUnit &&
      dropDownUnit &&
      selectedUnit?.unitId !== formValues?.selectedUnit
    ) {
      //unitSelectors.getUnit(store, unit.unitId);
      setSelectedUnit ? setSelectedUnit(dropDownUnit) : '';
      changeSetMapPositionToUnitsBounds(true);
    }
  }, [formValues?.selectedUnit]);

  // Intentar obtener el mapa solo si está disponible (componente dentro de MapContainer)
  let map = null;
  try {
    map = useMap();
  } catch (error) {
    // El componente no está dentro de MapContainer, no hay problema
  }

  const cleanup = () => {
    if (fetchInterval.current) clearInterval(fetchInterval.current);
  };

  useEffect(() => {
    return () => cleanup();
  }, []);

  const zoomMapToPositions = () => {
    if (
      map &&
      setMapPositionToPositionsBounds &&
      unit?.historicalPositions?.positions?.filter(
        (position, index) =>
          position?.latitude != null &&
          position?.latitude !== 0 &&
          position?.longitude != null &&
          position?.longitude !== 0
      ).length > 0
    ) {
      map.flyToBounds(
        latLngBounds(
          unit?.historicalPositions?.positions
            .filter(
              (position, index) =>
                position?.latitude != null &&
                position?.latitude !== 0 &&
                position?.longitude != null &&
                position?.longitude !== 0
            )
            .map((position) => latLng(position.latitude, position.longitude))
        ),
        {
          paddingBottomRight: [550, 0],
          paddingTopLeft: [0, 70],
          maxZoom: 15,
          animate: 15 !== map.getZoom()
        }
      );
    }
  };

  const displayObjects = () => {
    setHistoricalPositionsOnMapBounds({
      positions: unit?.historicalPositions?.positions?.filter(
        (position, index) =>
          showHistoricalPositions?.selectedPositionId === position.id ||
          (position?.latitude != null &&
            position?.latitude !== 0 &&
            position?.longitude != null &&
            position?.longitude !== 0 &&
            (map
              ? map.getBounds().contains(latLng(position.latitude, position.longitude))
              : true) &&
            (map?.getZoom() > 14 || (map?.getZoom() > 10 && index % 10 === 0) || index % 20 === 0))
      ),
      parkedPositions: unit?.historicalPositions?.parkedPositions?.filter(
        (position, index) =>
          showHistoricalPositions?.selectedPositionId === position.id ||
          (position?.latitude != null &&
            position?.latitude !== 0 &&
            position?.longitude != null &&
            position?.longitude !== 0 &&
            (map
              ? map.getBounds().contains(latLng(position.latitude, position.longitude))
              : true) &&
            (map?.getZoom() > 14 || (map?.getZoom() > 10 && index % 10 === 0) || index % 20 === 0))
      ),
      polyline:
        unit?.historicalPositions?.polyline?.length > 0
          ? map &&
            map.getBounds &&
            map
              .getBounds()
              .intersects(
                unit?.historicalPositions?.polyline?.map((position) => [
                  position.latitude,
                  position.longitude
                ])
              )
            ? unit?.historicalPositions.polyline
            : []
          : undefined,
      consoleEvents: unit?.consoleEvents
    });
  };

  // Ref para mantener displayObjects actualizado en el event listener del mapa
  const displayObjectsRef = useRef(displayObjects);
  useEffect(() => {
    displayObjectsRef.current = displayObjects;
  });

  // Registrar evento moveend solo si el mapa está disponible
  useEffect(() => {
    if (map) {
      const handleMoveEnd = () => {
        displayObjectsRef.current();
      };
      map.on('moveend', handleMoveEnd);
      return () => {
        map.off('moveend', handleMoveEnd);
      };
    }
  }, [map]);

  useEffect(() => {
    changeRerender(new Date());
    changeViewTable(false);
    setTimeout(() => changeViewTable(true), 200);
  }, [size]);

  useEffect(() => {
    if (map && selected.length > 0)
      map.flyToBounds(
        [[selected[selected.length - 1].latitude, selected[selected.length - 1].longitude]],
        {
          paddingBottomRight: [550, 0],
          paddingTopLeft: [0, 70],
          maxZoom: 15,
          animate: 15 !== map.getZoom()
        }
      );
    else zoomMapToPositions();
  }, [selected]);

  useEffect(() => {
    if (!isFetching) {
      zoomMapToPositions();
      displayObjects();
    }
  }, [isFetching]);

  useEffect(() => {
    if (unit?.unitId) {
      reset({
        dateRange: [
          formValues?.dateRange
            ? formValues?.dateRange[0]
            : moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          formValues?.dateRange
            ? formValues?.dateRange[1]
            : moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
        ],
        showUnitHistoryMarkers: true,
        groupPositionsIntoPraked: true,
        showUnitHistoryParkedMarkers: true,
        showUnitHeatMap: false
      });
    }
  }, [unit?.unitId, isSlidingPaneOpen, showHistoricalPositions?.show]);

  useEffect(() => {
    // Disparar fetch cuando el panel está abierto Y NO viene desde showHistoricalPositions
    // Si viene desde showHistoricalPositions (mapa de workplan), usar el otro useEffect que tiene las fechas correctas
    if (
      unit?.unitId &&
      isSlidingPaneOpen &&
      !showHistoricalPositions?.show && // <-- Solo cuando NO viene desde el mapa
      formValues &&
      formValues.dateRange?.length === 2 &&
      formValues?.groupPositionsIntoPraked != undefined
    ) {
      dispatch(
        unitActions.startFetchingUnitPositions(
          unit?.unitId,
          formValues.groupPositionsIntoPraked ? 2 : 1,
          formValues.dateRange[0],
          formValues.dateRange[1],
          UNIT_IDLE_SPEED
        )
      );
    }
  }, [
    unit?.unitId,
    isSlidingPaneOpen,
    showHistoricalPositions?.show,
    search,
    formValues?.groupPositionsIntoPraked,
    formValues?.dateRange
  ]);

  useEffect(() => {
    cleanup();

    if (
      unit?.unitId &&
      showHistoricalPositions &&
      showHistoricalPositions.show &&
      showHistoricalPositions.startDate &&
      showHistoricalPositions.startDate !== '' &&
      showHistoricalPositions.endDate &&
      showHistoricalPositions.endDate !== '' &&
      showHistoricalPositions.reportType
    ) {
      if (showHistoricalPositions?.useInterval) {
        fetchInterval.current = setInterval(() => {
          dispatch(
            unitActions.startFetchingUnitPositions(
              unit.unitId,
              showHistoricalPositions.reportType,
              showHistoricalPositions.startDate,
              showHistoricalPositions.endDate,
              showHistoricalPositions.idleSpeed || UNIT_IDLE_SPEED
            )
          );
        }, showHistoricalPositions.useInterval);
      }

      dispatch(
        unitActions.startFetchingUnitPositions(
          unit.unitId,
          showHistoricalPositions.reportType,
          showHistoricalPositions.startDate,
          showHistoricalPositions.endDate,
          showHistoricalPositions.idleSpeed || UNIT_IDLE_SPEED
        )
      );
    }
  }, [
    unit?.unitId,
    showHistoricalPositions?.show,
    showHistoricalPositions?.useInterval,
    showHistoricalPositions?.reportType,
    showHistoricalPositions?.startDate,
    showHistoricalPositions?.endDate,
    showHistoricalPositions?.idleSpeed
  ]);

  useEffect(() => {
    if (showHistoricalPositions?.selectedPositionId) displayObjects();
  }, [showHistoricalPositions?.selectedPositionId]);

  function getArrowImage(heading) {
    if (heading >= 337.5 || heading < 22.5) {
      return 'ArrowUp'; //Norte
    } else if (heading >= 22.5 && heading < 67.5) {
      return 'ArrowUpRight'; //Noreste
    } else if (heading >= 67.5 && heading < 112.5) {
      return 'ArrowRight'; //Este
    } else if (heading >= 112.5 && heading < 157.5) {
      return 'ArrowDownRight'; //Sureste
    } else if (heading >= 157.5 && heading < 202.5) {
      return 'ArrowDown'; //Sur
    } else if (heading >= 202.5 && heading < 247.5) {
      return 'ArrowDownLeft'; //Suroeste
    } else if (heading >= 247.5 && heading < 292.5) {
      return 'ArrowLeft'; //Oeste
    } else {
      return 'ArrowUpLeft'; //Noroeste
    }
  }

  // Function to create geofence from historical positions
  const createGeofenceFromHistoricalPositions = () => {
    const positions = unit?.historicalPositions?.positions;
    if (!positions || positions.length < 2) {
      return;
    }

    // Sort positions by date to ensure proper order
    const sortedPositions = orderBy(positions, 'gpsDate', 'asc');

    // Get first and last positions as point A and B
    const firstPosition = sortedPositions[0];
    const lastPosition = sortedPositions[sortedPositions.length - 1];

    // Create initial geofence data
    const initialGeofence = {
      geofenceName: `Geocerca ${unit.unitName} - ${moment(firstPosition.gpsDate).format(
        'YYYY-MM-DD'
      )}`,
      geofenceAddress: firstPosition.locationName || '',
      organizationId: unit.organizationId || user?.organizationId,
      divisionId: unit.divisionId,
      subdivisionId: unit.subdivisionId,
      shape: 'P', // Polygon shape for route geofence
      isRouteGeofence: true, // Flag to identify route geofence
      pointALatitude: firstPosition.latitude,
      pointALongitude: firstPosition.longitude,
      pointBLatitude: lastPosition.latitude,
      pointBLongitude: lastPosition.longitude,
      corridorWidth: 100, // Default corridor width in meters
      // Create a simple polygon from the positions for preview
      geofenceWktString: createPolygonFromPositions(sortedPositions)
    };

    setEditingGeofence(initialGeofence);
    setIsCreateGeofenceSlideOpen(true);
  };

  // Helper function to create a polygon from positions
  const createPolygonFromPositions = (positions) => {
    if (positions.length < 2) return '';

    // Create a simple corridor polygon from the positions
    const width = 0.001; // Small width for preview
    const coords = positions.map((pos) => [pos.longitude, pos.latitude]);

    // Create a simple rectangle around the path
    const minLng = Math.min(...coords.map((c) => c[0])) - width;
    const maxLng = Math.max(...coords.map((c) => c[0])) + width;
    const minLat = Math.min(...coords.map((c) => c[1])) - width;
    const maxLat = Math.max(...coords.map((c) => c[1])) + width;

    const polygon = [
      [minLng, minLat],
      [maxLng, minLat],
      [maxLng, maxLat],
      [minLng, maxLat],
      [minLng, minLat]
    ];

    const wktCoords = polygon.map((coord) => `${coord[0]} ${coord[1]}`).join(', ');
    return `POLYGON ((${wktCoords}))`;
  };

  const getBaseUrl = () => {
    if (env.PROD) {
      // Si hay una URL pública configurada, úsala
      if (env.BASE_URL) {
        return env.BASE_URL;
      }
      // Si no, construye la URL base incluyendo el pathname base
      const pathname = window.location.pathname.split('/')[1]; // obtiene el primer segmento del path
      return `${window.location.origin}/${pathname}`;
    }
    return window.location.origin;
  };

  const tableColumns: TableColumn[] = useMemo(
    () => [
      {
        fieldName: 'gpsDate',
        headerText: 'units.date',
        type: 'dateTime',
        formatColumn: 'yyyy-MM-dd HH:mm'
      },
      {
        fieldName: 'systemDate',
        headerText: 'units.receptionDate',
        type: 'dateTime',
        formatColumn: 'yyyy-MM-dd HH:mm'
      },
      { fieldName: 'eventName', headerText: 'units.event', type: 'string' },
      { fieldName: 'locationName', headerText: 'units.locationName', type: 'string' },
      {
        fieldName: 'speed',
        headerText: 'units.speed',
        type: 'number',
        formatColumn: '0,0',
        metric: user?.speedSystem
      },
      { fieldName: 'metric', headerText: 'units.metric', type: 'number', formatColumn: '0.00' },
      {
        fieldName: 'odometer',
        headerText: 'units.odometer',
        type: 'number',
        formatColumn: '0,0',
        metric: user?.distanceSystem
      },
      { fieldName: 'longitude', headerText: 'units.longitude', type: 'number' },
      { fieldName: 'latitude', headerText: 'units.latitude', type: 'number' },
      { fieldName: 'eventColor', headerText: 'units.eventColor', type: 'string' },
      { fieldName: 'eventTextColor', staticText: 'units.eventTextColor', type: 'string' },
      {
        fieldName: 'ignition',
        headerText: 'units.ignition',
        type: 'string',
        visible: unit?.hasIgnitionInput
      },
      { fieldName: 'heading', headerText: 'units.heading', type: 'number' },
      { fieldName: 'headingArrows', headerText: '', type: 'string' },
      {
        fieldName: 'distanceAllPositions',
        staticText: user?.distanceSystem || '',
        type: 'number',
        formatColumn: '0,0'
      }
    ],
    [user?.distanceSystem, unit?.hasIgnitionInput]
  );
  const reportDataSourceSummary = useMemo(
    () => ({
      title: intl.formatMessage({ id: 'units.historicalPositions.report.tittle' }),
      subtitle: `Id: ${unit.unitId} - ${unit.unitName}`,
      filters:
        (formValues.dateRange ? formValues.dateRange[0] : '') +
        ' - ' +
        (formValues.dateRange ? formValues.dateRange[1] : ''),
      dataSets: [
        {
          value: [
            {
              totalDistance: unit?.historicalPositions?.distanceFormat,
              distanceAllPositions:
                unit?.historicalPositions?.distance != null
                  ? parseFloat(unit.historicalPositions.distance).toFixed(2)
                  : '',
              unitId: unit?.unitId,
              unitName: unit?.unitName,
              licensePlate: unit?.licensePlate,
              dateRange:
                (formValues.dateRange ? formValues.dateRange[0] : '') +
                ' - ' +
                (formValues.dateRange ? formValues.dateRange[1] : ''),
              defaultMetricUnit: unit?.defaultMetricUnit != null ? unit.defaultMetricUnit : '',
              defaultMetricName: unit?.defaultMetricName != null ? unit.defaultMetricName : '',
              defaultMetricRoundFactor:
                unit?.defaultMetricRoundFactor != null ? unit.defaultMetricRoundFactor : ''
            }
          ],
          name: 'infoHeaderDataSet'
        },
        {
          value: orderBy(
            unit?.historicalPositions?.allPositions.map((position) => ({
              ...position,
              eventName: position.eventName || intl.formatMessage({ id: 'units.parked' }),
              headingArrows: `${getBaseUrl()}/assets/${getArrowImage(position.heading)}.png`,
              speed: position.speed === null ? '' : position.speed,
              metric:
                position.metric === null ||
                position.metric === undefined ||
                position.metric === '' ||
                position.metric === 'N/A' ||
                position.metric === 'NaN' ||
                isNaN(parseFloat(position.metric))
                  ? ''
                  : parseFloat(position.metric),
              ignition:
                position.isIgnitionOn === 1
                  ? intl.formatMessage({ id: 'units.on' })
                  : position.isIgnitionOn === 0
                    ? intl.formatMessage({ id: 'units.off' })
                    : '--'
            })),
            'gpsDate',
            'asc'
          ),
          name: 'HistoricalPositionUnits'
        },
        {
          value: [formatColumnsForDataSource(tableColumns)],
          name: 'Columns'
        }
      ]
    }),
    [
      intl,
      unit?.unitId,
      unit?.unitName,
      unit?.historicalPositions?.distanceFormat,
      unit?.historicalPositions?.distance,
      unit?.historicalPositions?.allPositions,
      formValues?.dateRange,
      tableColumns
    ]
  );

  return (
    <>
      {historicalPositionsOnMapBounds?.positions?.length > 0 &&
        (isSlidingPaneOpen || showHistoricalPositions?.show) &&
        (showHistoricalPositions?.show
          ? showHistoricalPositions?.showUnitHistoryMarkers
          : formValues?.showUnitHistoryMarkers) &&
        historicalPositionsOnMapBounds.positions.map((position, index) =>
          position.eventId === 0 ? (
            <RotatedMarker
              key={`${index}-${
                (selected.length > 0 && selected[selected.length - 1].id === position.id) ||
                showHistoricalPositions?.selectedPositionId === position.id
              }`}
              // pmIgnore={true}
              zIndexOffset={750}
              riseOnHover={true}
              rotationAngle={position.heading}
              rotationOrigin="center"
              icon={divIcon({
                className: 'custom-div-icon',
                html: `<svg viewBox="0 0 58 58">
                <polygon style="fill:${primary}; stroke:black; stroke-width: 2" 
                points="28.2,34.4 14.5,44.1 28.2,7.2 40.6,44.1 "/></svg>`,
                iconSize: [22, 22],
                iconAnchor: [11, 11]
              })}
              position={[position.latitude, position.longitude]}
              eventHandlers={{
                click: () => {}
              }}
            >
              <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
                  <div style={{ fontWeight: 'bold' }}>
                    {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                  {position.locationName && <div>{position.locationName}</div>}
                  {position.speed != null && (
                    <div>
                      {parsers
                        .convertKilometersPerHourToAuthUserSpeedSystem(position.speed)
                        .toFixed(0)}{' '}
                      {userSpeedFormat}
                    </div>
                  )}
                </div>
              </Tooltip>
              <Popup closeButton={true} closeOnClick={false} autoClose={false}>
                <div style={{ minWidth: '200px', fontSize: '11px' }}>
                  {!user?.unitsLabelContent ? (
                    // Si no hay configuración, mostrar valores por defecto
                    <>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>{intl.formatMessage({ id: 'trackingUnits.gpsDate' })}:</strong>{' '}
                        {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                      </div>
                      {position.systemDate && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'units.receptionDate' })}:</strong>{' '}
                          {moment(position.systemDate).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                      )}
                      {position.locationName && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                          {position.locationName}
                        </div>
                      )}
                      {position.speed != null && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'units.speed' })}:</strong>{' '}
                          {parsers
                            .convertKilometersPerHourToAuthUserSpeedSystem(position.speed)
                            .toFixed(2)}{' '}
                          {userSpeedFormat}
                        </div>
                      )}
                      {position.odometer != null && (
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong>{' '}
                          {parsers
                            .convertKilometersToAuthUserDistanceSystem(position.odometer)
                            .toFixed(2)}{' '}
                          {userDistanceFormat}
                        </div>
                      )}
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                        {position.latitude?.toFixed(6)}, {position.longitude?.toFixed(6)}
                      </div>
                    </>
                  ) : (
                    // Usar configuración del usuario
                    user.unitsLabelContent.split(',').map((l, idx) => (
                      <span key={idx}>
                        {l === '0' && position.locationName && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                            {position.locationName}
                          </div>
                        )}
                        {l === '1' && position.gpsDate && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'trackingUnits.gpsDate' })}:</strong>{' '}
                            {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                          </div>
                        )}
                        {l === '2' && position.systemDate && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.receptionDate' })}:</strong>{' '}
                            {moment(position.systemDate).format('YYYY-MM-DD HH:mm:ss')}
                          </div>
                        )}
                        {l === '3' && position.odometer != null && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong>{' '}
                            {parsers
                              .convertKilometersToAuthUserDistanceSystem(position.odometer)
                              .toFixed(2)}{' '}
                            {userDistanceFormat}
                          </div>
                        )}
                        {l === '4' && position.speed != null && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.speed' })}:</strong>{' '}
                            {parsers
                              .convertKilometersPerHourToAuthUserSpeedSystem(position.speed)
                              .toFixed(2)}{' '}
                            {userSpeedFormat}
                          </div>
                        )}
                      </span>
                    ))
                  )}
                  {!user?.unitsLabelContent &&
                    position?.locationName?.split(',').map((e, idx) => {
                      return (
                        <p
                          key={idx}
                          style={{ fontSize: '11px', margin: '0px', fontWeight: 'bold' }}
                        >
                          {e}
                        </p>
                      );
                    })}
                  {user?.unitsLabelContent?.split(',').map((l, idx) => (
                    <span key={idx}>
                      {l == '0' && (
                        <>
                          {position?.locationName?.split(',').map((e, idx2) => {
                            return (
                              <p
                                key={idx2}
                                style={{ fontSize: '11px', margin: '0px', fontWeight: 'bold' }}
                              >
                                {e}
                              </p>
                            );
                          })}
                        </>
                      )}
                      {l == '1' && (
                        <>
                          <span style={{ fontSize: '11px' }}>
                            {`${intl.formatMessage({ id: 'trackingUnits.gpsDate' })}: ${
                              unit.gpsDate
                                ? moment(position.gpsDate).format('YYYY-MM-DD HH:mm')
                                : 'N/A'
                            }`}
                          </span>
                        </>
                      )}
                      {l == '3' && (
                        <>
                          <br />
                          <span style={{ fontSize: '11px' }}>
                            {`${intl.formatMessage({ id: 'units.odometer' })}: ${
                              parsers
                                .convertKilometersToAuthUserDistanceSystem(position.odometer)
                                .toFixed(2) +
                                ' ' +
                                userDistanceFormat || 'N/A'
                            }`}
                          </span>
                        </>
                      )}
                      {l == '4' && (
                        <>
                          <br />
                          <span style={{ fontSize: '11px' }}>
                            {`${intl.formatMessage({ id: 'units.speed' })}: ${
                              parsers
                                .convertKilometersPerHourToAuthUserSpeedSystem(position.speed)
                                .toFixed(2) +
                                ' ' +
                                userSpeedFormat || 'N/A'
                            }`}
                          </span>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              </Popup>
            </RotatedMarker>
          ) : (
            (showHistoricalPositions?.showUnitHistoryOnlyNormalPositionsMarkers ?? false) ==
              false && (
              <Marker
                key={`${index}-${
                  (selected.length > 0 && selected[selected.length - 1].id === position.id) ||
                  showHistoricalPositions?.selectedPositionId === position.id
                }`}
                // pmIgnore={true}
                zIndexOffset={700}
                riseOnHover={true}
                icon={divIcon({
                  className: 'custom-div-icon',
                  html: renderToString(
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        background: 'white',
                        width: 30,
                        height: 30,
                        padding: 3,
                        borderRadius: '100%',
                        borderStyle: 'solid',
                        borderColor: `#${position.eventColor}`
                      }}
                    >
                      <Icon
                        icon={`Alert-${position.eventId}`}
                        color={`#${position.eventColor}`}
                        size={30}
                      />
                    </div>
                  ),
                  iconSize: [30, 30],
                  iconAnchor: [15, 15]
                })}
                position={[position.latitude, position.longitude]}
                eventHandlers={{
                  click: () => {}
                }}
              >
                <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                  <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                    </div>
                    {position.eventName && (
                      <div>
                        {position.eventName}
                        {position.eventParameter && ` (${position.eventParameter})`}
                        {position.paramAlerta && ` (${position.paramAlerta})`}
                      </div>
                    )}
                    {position.locationName && <div>{position.locationName}</div>}
                    {position.speed != null && (
                      <div>
                        {parsers
                          .convertKilometersPerHourToAuthUserSpeedSystem(position.speed)
                          .toFixed(0)}{' '}
                        {userSpeedFormat}
                      </div>
                    )}
                  </div>
                </Tooltip>
                <Popup closeButton={true} closeOnClick={false} autoClose={false}>
                  <div style={{ minWidth: '200px', fontSize: '11px' }}>
                    {!user?.unitsLabelContent ? (
                      // Si no hay configuración, mostrar valores por defecto
                      <>
                        <div style={{ marginBottom: '5px' }}>
                          <strong>{intl.formatMessage({ id: 'trackingUnits.gpsDate' })}:</strong>{' '}
                          {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                        </div>
                        {position.systemDate && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.receptionDate' })}:</strong>{' '}
                            {moment(position.systemDate).format('YYYY-MM-DD HH:mm:ss')}
                          </div>
                        )}
                        {position.eventName && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.event' })}:</strong>{' '}
                            {position.eventName}
                            {position.eventParameter && ` (${position.eventParameter})`}
                            {position.paramAlerta && ` (${position.paramAlerta})`}
                          </div>
                        )}
                        {position.locationName && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                            {position.locationName}
                          </div>
                        )}
                        {position.speed != null && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.speed' })}:</strong>{' '}
                            {parsers
                              .convertKilometersPerHourToAuthUserSpeedSystem(position.speed)
                              .toFixed(2)}{' '}
                            {userSpeedFormat}
                          </div>
                        )}
                        {position.odometer != null && (
                          <div style={{ marginBottom: '5px' }}>
                            <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong>{' '}
                            {parsers
                              .convertKilometersToAuthUserDistanceSystem(position.odometer)
                              .toFixed(2)}{' '}
                            {userDistanceFormat}
                          </div>
                        )}
                        <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                          {position.latitude?.toFixed(6)}, {position.longitude?.toFixed(6)}
                        </div>
                      </>
                    ) : (
                      // Usar configuración del usuario
                      user.unitsLabelContent.split(',').map((l, idx) => (
                        <span key={idx}>
                          {l === '0' && position.locationName && (
                            <div style={{ marginBottom: '5px' }}>
                              <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                              {position.locationName}
                            </div>
                          )}
                          {l === '1' && position.gpsDate && (
                            <div style={{ marginBottom: '5px' }}>
                              <strong>
                                {intl.formatMessage({ id: 'trackingUnits.gpsDate' })}:
                              </strong>{' '}
                              {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                            </div>
                          )}
                          {l === '2' && position.systemDate && (
                            <div style={{ marginBottom: '5px' }}>
                              <strong>{intl.formatMessage({ id: 'units.receptionDate' })}:</strong>{' '}
                              {moment(position.systemDate).format('YYYY-MM-DD HH:mm:ss')}
                            </div>
                          )}
                          {l === '3' && position.odometer != null && (
                            <div style={{ marginBottom: '5px' }}>
                              <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong>{' '}
                              {parsers
                                .convertKilometersToAuthUserDistanceSystem(position.odometer)
                                .toFixed(2)}{' '}
                              {userDistanceFormat}
                            </div>
                          )}
                          {l === '4' && position.speed != null && (
                            <div style={{ marginBottom: '5px' }}>
                              <strong>{intl.formatMessage({ id: 'units.speed' })}:</strong>{' '}
                              {parsers
                                .convertKilometersPerHourToAuthUserSpeedSystem(position.speed)
                                .toFixed(2)}{' '}
                              {userSpeedFormat}
                            </div>
                          )}
                        </span>
                      ))
                    )}
                    {position.eventName && (
                      <div
                        style={{
                          marginBottom: '5px',
                          marginTop: user?.unitsLabelContent ? '5px' : '0px'
                        }}
                      >
                        <strong>{intl.formatMessage({ id: 'units.event' })}:</strong>{' '}
                        {position.eventName}
                        {position.eventParameter && ` (${position.eventParameter})`}
                        {position.paramAlerta && ` (${position.paramAlerta})`}
                      </div>
                    )}
                  </div>
                </Popup>
              </Marker>
            )
          )
        )}
      {historicalPositionsOnMapBounds?.parkedPositions?.length > 0 &&
        (isSlidingPaneOpen || showHistoricalPositions?.show) &&
        (showHistoricalPositions?.show
          ? showHistoricalPositions?.showUnitHistoryParkedMarkers
          : formValues?.groupPositionsIntoPraked && formValues?.showUnitHistoryParkedMarkers) &&
        historicalPositionsOnMapBounds.parkedPositions.map((position, index) => (
          <Marker
            key={`${index}-${
              (selected.length > 0 && selected[selected.length - 1].id === position.id) ||
              showHistoricalPositions?.selectedPositionId === position.id
            }`}
            // pmIgnore={true}
            zIndexOffset={700}
            riseOnHover={true}
            icon={divIcon({
              className: 'custom-div-icon',
              html: renderToString(
                <Icon
                  icon={getIconByDatesDifference(position.endDate, position.gpsDate)}
                  size={48}
                />
              ),
              iconSize: [30, 30],
              iconAnchor: [15, 15]
            })}
            position={[position.latitude, position.longitude]}
            eventHandlers={{
              click: () => {}
            }}
          >
            <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
              <div style={{ fontSize: '10px', lineHeight: '1.3' }}>
                <div style={{ fontWeight: 'bold' }}>
                  {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                </div>
                <div>{intl.formatMessage({ id: 'units.parked' })}</div>
                {position.locationName && <div>{position.locationName}</div>}
              </div>
            </Tooltip>
            <Popup closeButton={true} closeOnClick={false} autoClose={false}>
              <div style={{ minWidth: '200px', fontSize: '11px' }}>
                <div style={{ marginBottom: '5px' }}>
                  <strong>
                    {intl.formatMessage({ id: 'trackingUnits.gpsDate' })} (
                    {intl.formatMessage({ id: 'units.start' })}):
                  </strong>{' '}
                  {moment(position.gpsDate).format('YYYY-MM-DD HH:mm:ss')}
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <strong>
                    {intl.formatMessage({ id: 'trackingUnits.gpsDate' })} (
                    {intl.formatMessage({ id: 'units.end' })}):
                  </strong>{' '}
                  {moment(position.endDate).format('YYYY-MM-DD HH:mm:ss')}
                </div>
                <div style={{ marginBottom: '5px' }}>
                  <strong>{intl.formatMessage({ id: 'units.duration' })}:</strong>{' '}
                  {getDatesDifferenceString(position.endDate, position.gpsDate, intl)}
                </div>
                {position.locationName && (
                  <div style={{ marginBottom: '5px' }}>
                    <strong>{intl.formatMessage({ id: 'units.locationName' })}:</strong>{' '}
                    {position.locationName}
                  </div>
                )}
                {position.odometer != null && (
                  <div style={{ marginBottom: '5px' }}>
                    <strong>{intl.formatMessage({ id: 'units.odometer' })}:</strong>{' '}
                    {parsers
                      .convertKilometersToAuthUserDistanceSystem(position.odometer)
                      .toFixed(2)}{' '}
                    {userDistanceFormat}
                  </div>
                )}
                <div style={{ fontSize: '10px', color: '#666', marginTop: '8px' }}>
                  {position.latitude?.toFixed(6)}, {position.longitude?.toFixed(6)}
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      {/* {unit?.historicalPositions?.polyline?.length > 0 && isSlidingPaneOpen && (
        <HeatmapLayer
          points={unit.historicalPositions.polyline.map((point) => ({
            ...point,
            intensity: 1
          }))}
          latitudeExtractor={(m: any) => m.latitude}
          longitudeExtractor={(m: any) => m.longitude}
          intensityExtractor={(m: any) => parseFloat(m.intensity)}
          max={1}
          minOpacity={0.2}
        />
      )} */}
      {historicalPositionsOnMapBounds?.polyline?.length > 0 &&
        (isSlidingPaneOpen || showHistoricalPositions?.show) && (
          <Polyline
            pathOptions={{ color: primary, weight: showHistoricalPositions?.lineWidth ?? 3 }}
            positions={historicalPositionsOnMapBounds.polyline.map((point) => [
              point.latitude,
              point.longitude
            ])}
            eventHandlers={{
              click: () => {}
            }}
          ></Polyline>
        )}
      {historicalPositionsOnMapBounds?.consoleEvents?.length > 0 &&
        showHistoricalPositions?.showUnitHistoryConsoleEventMarkers === true &&
        (isSlidingPaneOpen || showHistoricalPositions?.show) &&
        historicalPositionsOnMapBounds.consoleEvents.map((position, index) => (
          <Marker
            key={`${index}-${
              (selected.length > 0 && selected[selected.length - 1].id === position.id) ||
              showHistoricalPositions?.selectedPositionId === position.id
            }`}
            // pmIgnore={true}
            zIndexOffset={700}
            riseOnHover={true}
            icon={divIcon({
              className: 'custom-div-icon',
              html: renderToString(
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    background: 'white',
                    width: 30,
                    height: 30,
                    padding: 3,
                    borderRadius: '100%',
                    borderStyle: 'solid',
                    borderColor: `#${position.eventColor}`
                  }}
                >
                  <Icon
                    icon={`Alert-${position.eventId}`}
                    color={`#${position.eventColor}`}
                    size={30}
                  />
                </div>
              ),
              iconSize: [30, 30],
              iconAnchor: [15, 15]
            })}
            position={[position.latitude, position.longitude]}
            eventHandlers={{
              click: () => {}
            }}
          >
            <Tooltip
              permanent={
                (selected.length > 0 && selected[selected.length - 1].id === position.id) ||
                showHistoricalPositions?.selectedPositionId === position.id
              }
            >
              {user?.unitsLabelContent?.split(',').map((l) => (
                <>
                  {l == '0' && (
                    <>
                      <br />
                      <span>{!isEmpty(position.locationName) ? position.locationName : 'N/A'}</span>
                    </>
                  )}
                  {l == '1' && (
                    <>
                      <br />
                      <span>
                        {unit.gpsDate ? moment(position.gpsDate).format('YYYY-MM-DD HH:mm') : 'N/A'}
                      </span>
                    </>
                  )}
                  {l == '2' && (
                    <>
                      <br />
                      <span>
                        {position.lastCommunicationDate
                          ? moment(unit.lastCommunicationDate).format('YYYY-MM-DD HH:mm')
                          : 'N/A'}
                      </span>
                    </>
                  )}
                  {l == '3' && (
                    <>
                      <br />
                      <span>{position.odometerFormat || 'N/A'}</span>
                    </>
                  )}
                  {l == '4' && (
                    <>
                      <br />
                      <span>{position.speedFormat || 'N/A'}</span>
                    </>
                  )}
                </>
              ))}
              {/* <b style={{ fontSize: '11px' }}>
                {moment(position.gpsDate).format('YYYY-MM-DD HH:mm')}
              </b>
              <br />
              <b style={{ fontSize: '11px' }}>
                {intl.formatMessage({
                  id: `organizations.event${position.eventId}`
                })}
              </b>
              <br />
              <b style={{ fontSize: '11px' }}>{position.locationName}</b> */}
            </Tooltip>
          </Marker>
        ))}
      <SlidingPane
        className="custom-sliding-pane"
        overlayClassName={'custom-overlay-sliding-pane-map'}
        isOpen={isSlidingPaneOpen}
        title={title}
        subtitle={`Id: ${unit.unitId} - ${unit.unitName}`}
        width={size}
        onRequestClose={() => {
          if (closeSlidingPane) closeSlidingPane();
        }}
      >
        <>
          <Row>
            <Col sm="12">
              {unit && unit?.unitId && unit.organizationId && unit?.operatorId ? (
                <Controller
                  control={control}
                  name="selectedUnit"
                  defaultValue={unit?.unitId}
                  render={({ field, fieldState }) => (
                    <UnitsDropdown
                      field={field}
                      fieldstate={fieldState}
                      id={'unitDropdown'}
                      required
                      placeholder={intl.formatMessage({ id: 'units.searchUnit' })}
                      membership={{
                        operatorId: unit?.operatorId,
                        organizationId: unit?.organizationId
                      }}
                      unitsFilters={{
                        operatorId: unit?.operatorId,
                        organizationId: unit?.organizationId
                      }}
                      forceinitialvalue={unit?.unitId}
                    />
                  )}
                />
              ) : (
                ''
              )}
            </Col>
          </Row>
          <Row>
            <Col sm={9}>
              <Controller
                control={control}
                name="dateRange"
                defaultValue={[
                  moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                  moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')
                ]}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <DateInput
                    field={field}
                    fieldstate={fieldState}
                    maxDate={moment().endOf('day').format('YYYY-MM-DD HH:mm:ss')}
                    required
                    isRange
                    enableTime
                  />
                )}
              />
            </Col>
            <Col sm={3} className="mt-25">
              <Button.Ripple
                className="mt-1"
                color="primary"
                /*type="submit"*/
                onClick={handleSubmit(() => {
                  setSearch(new Date());
                })}
              >
                <FormattedMessage id="common.search" />
              </Button.Ripple>
            </Col>
            <Col sm={4} className={'d-flex justify-content-center'}>
              <Controller
                control={control}
                name={'showUnitHistoryMarkers'}
                defaultValue={true}
                render={({ field }) => (
                  <SwitchInput
                    field={field}
                    tooltipMessage={intl.formatMessage({ id: 'units.showUnitHistoryMarkers' })}
                    label={<MapPin color={primary} size={15} />}
                  />
                )}
              />
            </Col>
            <Col sm={4} className={'d-flex justify-content-center'}>
              <Controller
                control={control}
                name={'groupPositionsIntoPraked'}
                defaultValue={true}
                render={({ field }) => (
                  <SwitchInput
                    field={field}
                    tooltipMessage={intl.formatMessage({ id: 'units.groupPositionsIntoPraked' })}
                    label={<Icon icon={'Parking'} color={primary} size={15} />}
                  />
                )}
              />
            </Col>
            {formValues?.groupPositionsIntoPraked && (
              <Col sm={4} className={'d-flex justify-content-center'}>
                <Controller
                  control={control}
                  name={'showUnitHistoryParkedMarkers'}
                  defaultValue={true}
                  render={({ field }) => (
                    <SwitchInput
                      field={field}
                      tooltipMessage={intl.formatMessage({
                        id: 'units.showUnitHistoryParkedMarkers'
                      })}
                      label={<Icon icon={'LetterP'} color={primary} size={15} />}
                    />
                  )}
                />
              </Col>
            )}
            {/* <Col sm={3}>
              <Controller
                control={control}
                name={'showUnitHeatMap'}
                defaultValue={false}
                render={({ field }) => (
                  <SwitchInput
                    field={field}
                    tooltipMessage={intl.formatMessage({ id: 'units.showUnitHeatMap' })}
                    label={
                      <>
                        <Icon icon={'LetterH'} color={primary} size={15} />
                        <Icon
                          icon={'LetterM'}
                          color={primary}
                          size={15}
                          style={{ marginLeft: '-0.4rem' }}
                        />
                      </>
                    }
                  />
                )}
              />
            </Col> */}
            {isFetching && (
              <Col className={'my-5'} sm={12}>
                <div className={'d-flex justify-content-center my-5'}>
                  <Spinner color="primary" />
                </div>
              </Col>
            )}
            {!isFetching && (
              <Col sm={12}>
                <hr className={'full-width-slide-panel'} />
                <Label className="label-information">
                  {`${intl.formatMessage({ id: 'units.distance' })}:`}
                </Label>
                <Label className="label-value-information">
                  {unit?.historicalPositions?.distanceFormat}
                </Label>
                <hr className={'full-width-slide-panel'} />
                <Label className="label-information">
                  {`${intl.formatMessage({ id: 'units.speed' })} ${intl.formatMessage({ id: 'common.maximum' })}:`}
                </Label>
                <Label className="label-value-information">
                  {unit?.historicalPositions?.allPositions &&
                  unit.historicalPositions.allPositions.length > 0
                    ? (() => {
                        const maxSpeed = Math.max(
                          ...unit.historicalPositions.allPositions
                            .filter((pos) => pos.speed != null && !isNaN(pos.speed))
                            .map((pos) => pos.speed)
                        );
                        return maxSpeed > 0
                          ? `${parsers.convertKilometersPerHourToAuthUserSpeedSystem(maxSpeed).toFixed(2)} ${userSpeedFormat}`
                          : '0.00 ' + userSpeedFormat;
                      })()
                    : '0.00 ' + userSpeedFormat}
                </Label>
                <hr className={'full-width-slide-panel'} />
              </Col>
            )}
            {!isFetching && unit?.historicalPositions?.positions?.length > 0 && (
              <Col sm={12}>
                <Toolbar
                  buttons={[
                    <DefaultButton
                      id={'maximize'}
                      icon={size === '80rem' ? 'Minimize' : 'Maximize'}
                      handleClick={() => {
                        changeSize(size === '45rem' ? '80rem' : '45rem');
                      }}
                      tooltipMessage={intl.formatMessage({
                        id: size === '80rem' ? 'workplans.minimize' : 'workplans.maximize'
                      })}
                    ></DefaultButton>,
                    <DefaultButton
                      id={'exportKML'}
                      icon={'Download'}
                      handleClick={() => {
                        const positions = unit?.historicalPositions?.positions || [];
                        const parkedPositions = unit?.historicalPositions?.parkedPositions || [];
                        if (!positions.length && !parkedPositions.length) return;
                        generateAndDownloadKML(positions, {
                          unitName: unit?.name || unit?.unitName,
                          from: showHistoricalPositions?.startDate,
                          to: showHistoricalPositions?.endDate,
                          parkedPositions
                        });
                      }}
                      tooltipMessage={intl.formatMessage({ id: 'units.exportKML' })}
                    />,
                    ,
                    <ReportViewerButton
                      tooltipMessage={'reports.seeReport'}
                      channelName={'report_Historical_Position_Units'}
                      handleClick={() => {}}
                      reportDataSource={reportDataSourceSummary}
                      useEmbbededChannel={true}
                      reportPath={'NewHistoricalPositionUnits.rdl'}
                    />,
                    <DefaultButton
                      id={'createGeofence'}
                      icon={'Geofences'}
                      handleClick={createGeofenceFromHistoricalPositions}
                      tooltipMessage={intl.formatMessage({ id: 'geofences.createFromRoute' })}
                      permissions={[geofencePermissions.addGeofence]}
                      isLoading={isAddingGeofence}
                    />
                  ]}
                />
                {viewTable && (
                  <Table
                    id={'tableUnitsSlidingPaneUnitHistoricalPositionsReport1'}
                    enablePersistence={false}
                    rerender={rerender}
                    sortedColumn={{
                      sortedColumnIndex: size === '80rem' ? 0 : 2,
                      direction: 'Ascending'
                    }}
                    columns={
                      size === '80rem'
                        ? extraColumns
                            .filter(
                              (column) =>
                                column.visible !== false && column.headerText != 'null(null)'
                            )
                            .map((column) => column)
                        : columnInfo
                    }
                    onDeselect={() => changeSelected([])}
                    data={unit?.historicalPositions?.allPositions.map((position) => ({
                      ...position,
                      eventName: position.eventName || intl.formatMessage({ id: 'units.parked' }),
                      speed: position.speed === null ? '' : position.speed,
                      metric:
                        isNaN(position.metric) && position.metric === null ? '' : position.metric
                    }))}
                    selected={selected}
                    changeSelected={changeSelected}
                  />
                )}
              </Col>
            )}
          </Row>
        </>
      </SlidingPane>

      {/* Sliding Pane for creating geofence from route */}
      <SlidingPaneForm
        title={intl.formatMessage({ id: 'geofences.createFromRoute' })}
        isOpenSlidingPane={isCreateGeofenceSlideOpen}
        closeSlidingPaneForm={() => {
          setIsCreateGeofenceSlideOpen(false);
          setEditingGeofence(null);
        }}
        width="45rem"
        inputs={[
          {
            type: 'text',
            label: intl.formatMessage({ id: 'geofences.name' }),
            required: true,
            name: 'geofenceName',
            initialValue: editingGeofence?.geofenceName || '',
            additionalonchangefunction: (value) => {
              setEditingGeofence({
                ...editingGeofenceRef.current,
                geofenceName: value
              });
            }
          },
          {
            type: 'text',
            label: intl.formatMessage({ id: 'geofences.geofenceAddress' }),
            required: false,
            name: 'geofenceAddress',
            initialValue: editingGeofence?.geofenceAddress || '',
            additionalonchangefunction: (value) => {
              setEditingGeofence({
                ...editingGeofenceRef.current,
                geofenceAddress: value
              });
            }
          },
          {
            type: 'custom',
            name: 'mapPreview',
            component: () => (
              <>
                <hr className={'full-width-slide-panel'} />
                <p className="text-muted small">
                  <FormattedMessage
                    id="geofences.routePreviewDescription"
                    defaultMessage="Esta geocerca se creará siguiendo el tramo del histórico de posiciones de la unidad. Los puntos A y B se han establecido automáticamente como la primera y última posición del recorrido."
                  />
                </p>
                <div className="mb-3">
                  <strong>
                    <FormattedMessage id="geofences.pointA" defaultMessage="Punto A" />:
                  </strong>{' '}
                  {editingGeofence?.pointALatitude?.toFixed(6)},{' '}
                  {editingGeofence?.pointALongitude?.toFixed(6)}
                </div>
                <div className="mb-3">
                  <strong>
                    <FormattedMessage id="geofences.pointB" defaultMessage="Punto B" />:
                  </strong>{' '}
                  {editingGeofence?.pointBLatitude?.toFixed(6)},{' '}
                  {editingGeofence?.pointBLongitude?.toFixed(6)}
                </div>
                <div className="mb-3">
                  <label htmlFor="corridorWidth">
                    <FormattedMessage
                      id="geofences.corridorWidth"
                      defaultMessage="Ancho del corredor (metros)"
                    />
                    :
                  </label>
                  <input
                    type="number"
                    id="corridorWidth"
                    className="form-control"
                    min="10"
                    max="1000"
                    step="10"
                    value={editingGeofence?.corridorWidth || 100}
                    onChange={(e) => {
                      setEditingGeofence({
                        ...editingGeofenceRef.current,
                        corridorWidth: parseInt(e.target.value) || 100
                      });
                    }}
                  />
                </div>
                <hr className={'full-width-slide-panel'} />
              </>
            )
          },
          {
            type: 'membership',
            name: 'membership',
            options: {
              colSize: '12',
              useOperator: false,
              useUserOrganization: true,
              setUserOrganization: true,
              showAllDivision: true,
              showAllSubdivision: true
            },
            initialValue: {
              operatorId: user?.operatorId,
              organizationId: editingGeofence?.organizationId || user?.organizationId,
              divisionId: editingGeofence?.divisionId,
              subdivisionId: editingGeofence?.subdivisionId
            },
            additionalonchangefunction: (values) => {
              setEditingGeofence({
                ...editingGeofenceRef.current,
                membership: values,
                ...values
              });
            }
          }
        ]}
        action={(values) => {
          const geofence = editingGeofenceRef.current;
          if (!geofence) return;

          // Create the geofence following the historical positions path
          const positions = unit?.historicalPositions?.positions;
          if (!positions || positions.length < 2) return;

          const sortedPositions = orderBy(positions, 'gpsDate', 'asc');

          // Generate corridor polygon following the exact path
          const corridorPolygon = createCorridorFromPositions(
            sortedPositions,
            geofence.corridorWidth || 100
          );

          const {
            vertexCount,
            longitude,
            longitude2,
            latitude,
            latitude2,
            vertexCsv,
            geofenceWKT
          } = getExtraGeofenceSaveValues({
            ...geofence,
            geofenceWktString: corridorPolygon
          });

          const routeGeofenceData = {
            pointALatitude: geofence.pointALatitude,
            pointALongitude: geofence.pointALongitude,
            pointBLatitude: geofence.pointBLatitude,
            pointBLongitude: geofence.pointBLongitude,
            corridorWidth: geofence.corridorWidth,
            isRouteGeofence: true
          };

          dispatch(
            geofenceActions.startAddingGeofence({
              ...geofence,
              ...values,
              ...values.membership,
              ...routeGeofenceData,
              geofenceId: uuidv4(),
              geofenceWktString: corridorPolygon,
              vertexCount,
              latitude,
              latitude2,
              longitude,
              longitude2,
              vertexCsv
            })
          );

          setIsCreateGeofenceSlideOpen(false);
          setEditingGeofence(null);
        }}
      />
    </>
  );
};

// Helper function to create corridor polygon from positions
const createCorridorFromPositions = (positions, corridorWidth) => {
  if (positions.length < 2) return '';

  const halfWidth = corridorWidth / 2;
  const leftSide = [];
  const rightSide = [];

  // Convert positions to LatLng-like objects for calculations
  const path = positions.map((pos) => ({
    lat: pos.latitude,
    lng: pos.longitude,
    bearingTo: function (other) {
      const lat1 = (this.lat * Math.PI) / 180;
      const lat2 = (other.lat * Math.PI) / 180;
      const deltaLng = ((other.lng - this.lng) * Math.PI) / 180;

      const y = Math.sin(deltaLng) * Math.cos(lat2);
      const x =
        Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);

      const bearing = (Math.atan2(y, x) * 180) / Math.PI;
      return (bearing + 360) % 360;
    },
    destinationPoint: function (distance, bearing) {
      const R = 6371e3; // Earth radius in meters
      const lat1 = (this.lat * Math.PI) / 180;
      const lng1 = (this.lng * Math.PI) / 180;
      const brng = (bearing * Math.PI) / 180;

      const lat2 = Math.asin(
        Math.sin(lat1) * Math.cos(distance / R) +
          Math.cos(lat1) * Math.sin(distance / R) * Math.cos(brng)
      );
      const lng2 =
        lng1 +
        Math.atan2(
          Math.sin(brng) * Math.sin(distance / R) * Math.cos(lat1),
          Math.cos(distance / R) - Math.sin(lat1) * Math.sin(lat2)
        );

      return {
        lat: (lat2 * 180) / Math.PI,
        lng: (lng2 * 180) / Math.PI
      };
    }
  }));

  for (let i = 0; i < path.length; i++) {
    const currentPoint = path[i];
    let bearing = 0;

    if (i === 0 && path.length > 1) {
      bearing = currentPoint.bearingTo(path[i + 1]);
    } else if (i === path.length - 1) {
      bearing = path[i - 1].bearingTo(currentPoint);
    } else {
      bearing = currentPoint.bearingTo(path[i + 1]);
    }

    const leftBearing = (bearing - 90 + 360) % 360;
    const rightBearing = (bearing + 90) % 360;

    const leftPoint = currentPoint.destinationPoint(halfWidth, leftBearing);
    const rightPoint = currentPoint.destinationPoint(halfWidth, rightBearing);

    leftSide.push(leftPoint);
    rightSide.push(rightPoint);
  }

  // Create the polygon: left side + right side reversed
  const corridor = [];
  corridor.push(...leftSide);
  corridor.push(...rightSide.reverse());
  corridor.push(leftSide[0]); // Close the polygon

  const wktCoords = corridor.map((coord) => `${coord.lng} ${coord.lat}`).join(', ');
  return `POLYGON ((${wktCoords}))`;
};

SlidingPaneUnitHistoricalPositionsReport.defaultProps = {
  closeSlidingPane: () => null,
  isSlidingPaneOpen: false,
  showHistoricalPositions: { show: false },
  title: '',
  unit: {}
};

export default SlidingPaneUnitHistoricalPositionsReport;
